/**
 * STORY-NEW-043: Padronização de UI dos Modais de Campanhas
 *
 * Componentes base para layout consistente de modais de edição.
 */

import { Button } from "@/components/ui/button";
import { Loader2, AlertCircle } from "lucide-react";
import { ReactNode } from "react";

// ========================================
// MODAL LAYOUT - Wrapper principal
// ========================================

interface CampaignEditModalLayoutProps {
  children: ReactNode;
  onSubmit: () => void;
  onCancel: () => void;
  isLoading?: boolean;
  submitLabel?: string;
  cancelLabel?: string;
  submitDisabled?: boolean;
}

export function CampaignEditModalLayout({
  children,
  onSubmit,
  onCancel,
  isLoading = false,
  submitLabel = "Salvar Alterações",
  cancelLabel = "Cancelar",
  submitDisabled = false,
}: CampaignEditModalLayoutProps) {
  return (
    <div className="flex flex-col h-full min-h-0 w-[min(900px,90vw)] max-w-full">
      {/* Scrollable Content — native overflow, parent provides definite height */}
      <div className="flex-1 min-h-0 min-w-0 overflow-y-auto overflow-x-hidden pr-2 md:pr-4">
        <div className="space-y-6 pb-4 min-w-0">
          {children}
        </div>
      </div>

      {/* Footer — fixed at bottom of flex container */}
      <div className="flex flex-col-reverse md:flex-row md:justify-end gap-2 pt-4 border-t mt-4 bg-background flex-shrink-0">
        <Button
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
          className="w-full md:w-auto"
        >
          {cancelLabel}
        </Button>
        <Button
          onClick={onSubmit}
          disabled={isLoading || submitDisabled}
          className="w-full md:w-auto"
        >
          {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}

// ========================================
// MODAL LOADING STATE
// ========================================

export function ModalLoadingState() {
  return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
    </div>
  );
}

// ========================================
// MODAL ERROR STATE
// ========================================

interface ModalErrorStateProps {
  message?: string;
  details?: string;
}

export function ModalErrorState({
  message = "Erro ao carregar campanha",
  details,
}: ModalErrorStateProps) {
  return (
    <div className="flex flex-col items-center justify-center h-64 gap-2">
      <AlertCircle className="h-8 w-8 text-destructive" />
      <p className="text-destructive font-medium">{message}</p>
      {details && (
        <p className="text-sm text-muted-foreground text-center max-w-md">
          {details}
        </p>
      )}
    </div>
  );
}

// ========================================
// MODAL SECTION - Seção com título
// ========================================

interface ModalSectionProps {
  title?: string;
  children: ReactNode;
  className?: string;
}

export function ModalSection({
  title,
  children,
  className = "",
}: ModalSectionProps) {
  return (
    <div className={`space-y-2 ${className}`}>
      {title && (
        <p className="text-sm font-medium text-muted-foreground">{title}</p>
      )}
      {children}
    </div>
  );
}

// ========================================
// MODAL FIELD GROUP - Grupo de campos
// ========================================

interface ModalFieldGroupProps {
  children: ReactNode;
  columns?: 1 | 2 | 3;
  className?: string;
}

export function ModalFieldGroup({
  children,
  columns = 2,
  className = "",
}: ModalFieldGroupProps) {
  const gridClass = {
    1: "grid-cols-1",
    2: "grid-cols-1 md:grid-cols-2",
    3: "grid-cols-1 md:grid-cols-2 lg:grid-cols-3",
  }[columns];

  return (
    <div className={`grid ${gridClass} gap-4 ${className}`}>
      {children}
    </div>
  );
}

// ========================================
// MODAL INFO BOX - Caixa de informação
// ========================================

interface ModalInfoBoxProps {
  children: ReactNode;
  variant?: "default" | "warning" | "success";
  className?: string;
}

export function ModalInfoBox({
  children,
  variant = "default",
  className = "",
}: ModalInfoBoxProps) {
  const variantClass = {
    default: "bg-gray-50 border",
    warning: "bg-yellow-50 border border-yellow-200",
    success: "bg-green-50 border border-green-200",
  }[variant];

  return (
    <div className={`rounded-lg p-3 ${variantClass} ${className}`}>
      {children}
    </div>
  );
}
